import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { ArrowLeft, ArrowRight, Check, Loader2, LayoutDashboard } from "lucide-react";
import { api, calcEnv } from "@/lib/api";
import { computeLayout } from "@/lib/geo";
import { computeBOM } from "@/lib/bom";
import { fmt } from "@/lib/catalog";
import { AppHeader } from "@/components/AppHeader";
import { Stepper } from "@/components/wizard/Stepper";
import { Button } from "@/components/ui/button";
import StepProject from "@/components/wizard/StepProject";
import StepLoads from "@/components/wizard/StepLoads";
import RoofEditor from "@/editor/RoofEditor";
import StepResults from "@/components/wizard/StepResults";

import { tr } from "@/lib/i18n";
const Step3 = (props) => <RoofEditor {...props} phase="roof" />;
const Step4 = (props) => <RoofEditor {...props} phase="modules" />;
const STEPS = [StepProject, StepLoads, Step3, Step4, StepResults];

const summaryOf = (p) => {
  const s = computeBOM(p, computeLayout(p)).stats;
  return { modules: s.modules, kwp: s.kwp };
};

export default function ProjectWizard() {
  const { id } = useParams();
  const nav = useNavigate();
  const [p, setP] = useState(null);
  const [saveState, setSaveState] = useState("saved");
  const latest = useRef(null);
  const timer = useRef(null);

  useEffect(() => {
    api.get(`/projects/${id}`).then(({ data }) => { latest.current = data; setP(data); }).catch(() => nav("/"));
  }, [id, nav]);

  const save = useCallback(async () => {
    const cur = latest.current;
    setSaveState("saving");
    await api.put(`/projects/${id}`, { ...cur, summary: summaryOf(cur) });
    setSaveState("saved");
  }, [id]);

  const update = useCallback((patch) => {
    const next = { ...latest.current, ...(typeof patch === "function" ? patch(latest.current) : patch) };
    latest.current = next;
    setP(next);
    setSaveState("pending");
    clearTimeout(timer.current);
    timer.current = setTimeout(save, 700);
  }, [save]);

  useEffect(() => () => { if (timer.current) { clearTimeout(timer.current); save(); } }, [save]);

  const needsEnv = p && p.env?.wind_load === undefined;
  useEffect(() => {
    if (!needsEnv) return;
    const t = setTimeout(() => calcEnv(latest.current).then((env) => update({ env })).catch(() => {}), 400);
    return () => clearTimeout(t);
  }, [needsEnv, p?.region, p?.province, p?.altitude, p?.building_height, update]);

  const layout = useMemo(() => (p ? computeLayout(p) : null), [p]);
  const bom = useMemo(() => (p ? computeBOM(p, layout) : null), [p, layout]);

  if (!p) return <div className="h-screen grid place-items-center text-slate-400"><Loader2 className="w-6 h-6 animate-spin" /></div>;

  const goto = (step) => update((prev) => ({ step, max_step: Math.max(prev.max_step || 1, step) }));
  const Step = STEPS[p.step - 1];
  const wide = p.step === 3 || p.step === 4;

  return (
    <div className="h-screen flex flex-col bg-slate-50 overflow-hidden">
      <AppHeader>
        <div className="min-w-0 text-right hidden md:block">
          <div className="font-semibold text-slate-900 truncate" data-testid="wizard-project-name">{p.name}</div>
          <div className="text-xs text-slate-500 flex items-center justify-end gap-1" data-testid="autosave-status">
            {saveState === "saved" ? <><Check className="w-3 h-3 text-emerald-600" /> {tr("Salvato automaticamente")}</> : <><Loader2 className="w-3 h-3 animate-spin" /> {tr("Salvataggio…")}</>}
          </div>
        </div>
        <Button variant="outline" data-testid="back-dashboard-btn" onClick={async () => { clearTimeout(timer.current); await save(); nav("/"); }}>
          <LayoutDashboard className="w-4 h-4 mr-1.5" /> {tr("Dashboard")}
        </Button>
      </AppHeader>
      <Stepper current={p.step} maxStep={p.max_step} onSelect={goto} />
      <div className={`flex-1 min-h-0 ${wide ? "" : "overflow-y-auto"}`}>
        <Step p={p} update={update} layout={layout} bom={bom} />
      </div>
      <footer className="h-16 shrink-0 bg-white border-t border-slate-200 flex items-center px-4 sm:px-6 gap-6 z-[500]">
        <div className="hidden md:flex items-center gap-6 text-sm font-tech text-slate-600" data-testid="status-bar">
          <span><span className="text-slate-400 uppercase">{tr("Moduli")}</span> <b className="text-slate-900" data-testid="status-modules">{bom.stats.modules}</b></span>
          <span><span className="text-slate-400 uppercase">{tr("Potenza")}</span> <b className="text-slate-900" data-testid="status-kwp">{fmt(bom.stats.kwp)} kWp</b></span>
          {p.region && !p.engine?.country && <>
            <span><span className="text-slate-400">{tr("VENTO NTC")}</span> <b className="text-slate-900">Z{p.env?.wind_zone ?? "–"}</b></span>
            <span><span className="text-slate-400">{tr("NEVE NTC")}</span> <b className="text-slate-900">{p.env?.snow_zone ?? "–"}</b></span>
          </>}
        </div>
        <div className="flex-1" />
        <Button variant="outline" className="h-11 px-5" data-testid="wizard-prev-btn" disabled={p.step === 1} onClick={() => goto(p.step - 1)}>
          <ArrowLeft className="w-4 h-4 mr-1.5" /> {tr("Indietro")}
        </Button>
        {p.step < 5 && (
          <Button className="h-11 px-6 bg-[#0F52BA] hover:bg-[#0D47A1]" data-testid="wizard-next-btn" onClick={() => goto(p.step + 1)}>
            {tr("Avanti")} <ArrowRight className="w-4 h-4 ml-1.5" />
          </Button>
        )}
      </footer>
    </div>
  );
}
